import { applySseProgressEvent } from "@/lib/contract-review-sse";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api-config";
import { getJwtToken } from "@/lib/auth-client";
import type { ProgressState, SSEEventData } from "@/types";

export type ReviewExecutionDto = {
  id: string;
  orgId: string;
  userId: string;
  resourceId: string;
  threadId: string;
  conversationId: string | null;
  operationKey: string;
  workflowRunId: string;
  status: "running" | "completed" | "failed" | "cancelled";
  step: string | null;
  progress: number | null;
  message: string | null;
  reviewId: string | null;
  error: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export function progressStateFromExecution(execution: ReviewExecutionDto): ProgressState {
  const message = execution.message || execution.error || "Contract review";
  return {
    message,
    step: execution.step ?? undefined,
    progress: execution.progress ?? (execution.status === "completed" ? 100 : 0),
    isConnected: execution.status === "running",
    logs: message
      ? [{ timestamp: new Date(), message, step: execution.step ?? undefined }]
      : [],
  };
}

export async function fetchReviewExecution(params: {
  conversationId: string;
  workflowRunId?: string | null;
}): Promise<ReviewExecutionDto | null> {
  const search = new URLSearchParams({ conversationId: params.conversationId });
  if (params.workflowRunId) search.set("workflowRunId", params.workflowRunId);
  const data = await apiFetch<{ execution: ReviewExecutionDto | null }>(
    `/api/supervisor-chat/review-execution?${search.toString()}`
  );
  return data.execution ?? null;
}

export async function cancelReviewExecution(workflowRunId: string): Promise<ReviewExecutionDto | null> {
  const data = await apiFetch<{ execution: ReviewExecutionDto }>(
    "/api/supervisor-chat/review-execution/cancel",
    {
      method: "POST",
      body: JSON.stringify({ workflowRunId }),
    }
  );
  return data.execution ?? null;
}

/**
 * Observe a detached review run via SSE. Closing/aborting only stops listening —
 * it does not cancel the backend review.
 */
export async function observeReviewExecution(params: {
  workflowRunId: string;
  signal?: AbortSignal;
  onProgress: (state: ProgressState) => void;
  onComplete?: (reviewId?: string | null) => void;
  onError?: (message: string) => void;
}): Promise<void> {
  const token = await getJwtToken();
  const url = apiUrl(
    `/api/supervisor-chat/review-execution/observe?workflowRunId=${encodeURIComponent(params.workflowRunId)}`
  );
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal: params.signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`Failed to observe review execution (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let event = "message";
  let progress: ProgressState = {
    message: "",
    progress: 0,
    isConnected: true,
    logs: [],
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith("event: ")) {
        event = trimmed.slice(7).trim();
        continue;
      }
      if (!trimmed.startsWith("data: ")) continue;
      let data: SSEEventData;
      try {
        data = JSON.parse(trimmed.slice(6)) as SSEEventData;
      } catch {
        continue;
      }
      if (event === "progress") {
        progress = applySseProgressEvent(progress, data);
        params.onProgress(progress);
      } else if (event === "complete") {
        params.onProgress({ ...progress, isConnected: false, progress: 100, message: "Review complete" });
        params.onComplete?.(typeof data.reviewId === "string" ? data.reviewId : null);
        return;
      } else if (event === "error") {
        const message =
          (typeof data.error === "string" && data.error) ||
          (typeof data.message === "string" && data.message) ||
          "Review failed";
        params.onProgress({
          ...progress,
          isConnected: false,
          message,
          step: "error",
        });
        params.onError?.(message);
        return;
      }
    }
  }
}
